import type { JSONContent } from '@tiptap/core';

const TRAILING_MEDIA_TYPES = new Set(['image', 'video', 'linkEmbed', 'linkCard']);

// Media added while the cursor is in a list lands inside the last list item.
const LIST_TYPES = new Set(['bulletList', 'orderedList', 'listItem']);

export interface SplitTrailingMediaResult {
  body: JSONContent;
  trailingMedia: JSONContent[];
}

interface SplitNodesResult {
  bodyNodes: JSONContent[];
  trailingMedia: JSONContent[];
}

const isTrailingMedia = (node: JSONContent): boolean => TRAILING_MEDIA_TYPES.has(node.type ?? '');

// An emoji or mention is content, so only whitespace and line breaks leave a paragraph blank.
const isBlankParagraph = (node: JSONContent): boolean =>
  node.type === 'paragraph' &&
  (node.content ?? []).every((child) => child.type === 'hardBreak' || (child.type === 'text' && !child.text?.trim()));

const isList = (node: JSONContent): boolean => LIST_TYPES.has(node.type ?? '');

const splitNodes = (nodes: JSONContent[]): SplitNodesResult => {
  let bodyLength = nodes.length;
  while (bodyLength > 0 && (isTrailingMedia(nodes[bodyLength - 1]) || isBlankParagraph(nodes[bodyLength - 1]))) {
    bodyLength -= 1;
  }

  const bodyNodes = nodes.slice(0, bodyLength);
  const trailingMedia = nodes.slice(bodyLength).filter(isTrailingMedia);
  const lastNode = bodyNodes.at(-1);
  if (!lastNode || !isList(lastNode)) return { bodyNodes, trailingMedia };

  const nested = splitNodes(lastNode.content ?? []);
  if (nested.trailingMedia.length === 0) return { bodyNodes, trailingMedia };

  const nodesBeforeList = bodyNodes.slice(0, -1);
  if (nested.bodyNodes.length > 0) {
    return {
      bodyNodes: [...nodesBeforeList, { ...lastNode, content: nested.bodyNodes }],
      trailingMedia: [...nested.trailingMedia, ...trailingMedia],
    };
  }

  // A list or list item that held only media leaves no empty bullet behind.
  const remaining = splitNodes(nodesBeforeList);
  return {
    bodyNodes: remaining.bodyNodes,
    trailingMedia: [...remaining.trailingMedia, ...nested.trailingMedia, ...trailingMedia],
  };
};

// Media with no text after it renders outside the body, so read more clamps only the text above it.
// Media followed by text stays in the body.
export const splitTrailingMedia = (tiptapState: JSONContent | null | undefined): SplitTrailingMediaResult => {
  if (!tiptapState) return { body: { type: 'doc', content: [] }, trailingMedia: [] };

  const { bodyNodes, trailingMedia } = splitNodes(tiptapState.content ?? []);
  if (trailingMedia.length === 0) return { body: tiptapState, trailingMedia };

  return { body: { ...tiptapState, content: bodyNodes }, trailingMedia };
};
